import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../../services/api';

const statusClass = { completed: 'badge-active', pending: 'badge-pending', failed: 'badge-suspended', refunded: 'badge-suspended' };
const statusLabel = { completed: 'Complété', pending: 'En attente', failed: 'Échoué', refunded: 'Remboursé' };

const TransactionsAdmin = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const fromState = location.state;

  const [txns, setTxns] = useState([]);
  const [b2bTxns, setB2bTxns] = useState([]);
  const [consumeTxns, setConsumeTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(fromState?.tab || 'b2c');
  const [search, setSearch] = useState(fromState?.search || '');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const [b2cRes, b2bRes, consumeRes] = await Promise.all([
        API.get('/admin/transactions', { params: { type: 'b2c', search, status: filterStatus } }),
        API.get('/admin/transactions', { params: { type: 'b2b' } }),
        API.get('/admin/transactions', { params: { type: 'consume', search } }),
      ]);
      setTxns(b2cRes.data.transactions || []);
      setB2bTxns(b2bRes.data.transactions || []);
      setConsumeTxns(consumeRes.data.transactions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filterStatus]);

  useEffect(() => {
    const t = setTimeout(fetchTransactions, 300);
    return () => clearTimeout(t);
  }, [fetchTransactions]);

  const totals = {
    volumeB2C: txns.filter(t => t.status === 'completed').reduce((a, t) => a + parseFloat(t.amount || 0), 0),
    commissions: txns.filter(t => t.status === 'completed').reduce((a, t) => a + parseFloat(t.commission || 0), 0),
    volumeB2B: b2bTxns.reduce((a, t) => a + parseFloat(t.amount || 0), 0),
    countB2C: txns.length,
    pointsConsumed: consumeTxns.reduce((a, t) => a + (t.points_amount || 0), 0),
  };

  return (
    <div>
      {/* Bandeau de contexte si navigation depuis partenaire/client */}
      {fromState?.from && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#eff6ff', border: '1px solid #bfdbfe',
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: '#1d4ed8',
        }}>
          <i className={`fas ${fromState.from === 'partner' ? 'fa-building' : 'fa-user'}`}></i>
          <span>
            Historique des transactions de&nbsp;<strong>{fromState.label}</strong>
            &nbsp;— filtre appliqué automatiquement
          </span>
          <button
            onClick={() => { setSearch(''); navigate('/dashboard/admin/transactions', { replace: true }); }}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 600,
            }}
            title="Effacer le filtre"
          >
            <i className="fas fa-times"></i> Effacer
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="admin-kpi-grid">
        {[
          { icon: 'fa-exchange-alt', label: 'Transactions B2C', value: totals.countB2C, color: '#3b82f6' },
          { icon: 'fa-coins', label: 'Volume B2C', value: `${totals.volumeB2C.toFixed(2)} TND`, color: '#10b981' },
          { icon: 'fa-percentage', label: 'Commissions', value: `${totals.commissions.toFixed(2)} TND`, color: '#f59e0b' },
          { icon: 'fa-shopping-cart', label: 'Volume B2B', value: `${totals.volumeB2B.toFixed(2)} TND`, color: '#8b5cf6' },
          { icon: 'fa-fire', label: 'Points consommés (API)', value: `${totals.pointsConsumed.toLocaleString('fr')} pts`, color: '#ef4444' },
        ].map((k, i) => (
          <div key={i} className="admin-kpi-card">
            <div className="admin-kpi-header">
              <div className="admin-kpi-icon" style={{ background: `${k.color}18`, color: k.color }}>
                <i className={`fas ${k.icon}`}></i>
              </div>
            </div>
            <div className="admin-kpi-value">{k.value}</div>
            <div className="admin-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['b2c', 'Transactions B2C'], ['b2b', 'Achats B2B'], ['consume', 'Consommations API']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
              background: tab === key ? '#3b82f6' : '#fff',
              color: tab === key ? '#fff' : '#64748b',
              fontWeight: 600, fontSize: 13
            }}
          >{label}</button>
        ))}
      </div>

      {/* Filters (B2C + Consume) */}
      {(tab === 'b2c' || tab === 'consume') && (
        <div className="admin-filters-bar">
          <input className="admin-filter-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
          {tab === 'b2c' && (
            <select className="admin-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="completed">Complété</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoué</option>
              <option value="refunded">Remboursé</option>
            </select>
          )}
          <button className="admin-btn-secondary" onClick={fetchTransactions}><i className="fas fa-sync-alt"></i></button>
        </div>
      )}

      {/* Table */}
      <div className="admin-table-card">
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 20, color: '#3b82f6', display: 'block', marginBottom: 8 }}></i>
            Chargement...
          </div>
        ) : tab === 'b2c' ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Partenaire</th>
                <th>Client</th>
                <th>Service</th>
                <th>Montant</th>
                <th>Points</th>
                <th>Commission</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {txns.map(t => (
                <tr key={t.id}>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>#{t.id}</code></td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{new Date(t.created_at).toLocaleString('fr')}</td>
                  <td>{t.partner_name}</td>
                  <td style={{ color: '#64748b' }}>{t.client_name || '-'}</td>
                  <td style={{ color: '#64748b' }}>{t.service_name || '-'}</td>
                  <td><strong>{t.amount ? `${parseFloat(t.amount).toFixed(2)} TND` : '-'}</strong></td>
                  <td>{t.points_amount?.toLocaleString('fr')} pts</td>
                  <td style={{ color: t.commission > 0 ? '#10b981' : '#94a3b8' }}>
                    {t.commission > 0 ? `${parseFloat(t.commission).toFixed(2)} TND` : '-'}
                  </td>
                  <td><span className={statusClass[t.status] || 'badge-inactive'}>{statusLabel[t.status] || t.status}</span></td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Aucune transaction</td></tr>
              )}
            </tbody>
          </table>
        ) : tab === 'consume' ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Partenaire</th>
                <th>Client</th>
                <th>Email</th>
                <th>Points consommés</th>
                <th>Clé idempotence</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {consumeTxns.map(t => (
                <tr key={t.id}>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>#{t.id}</code></td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{new Date(t.created_at).toLocaleString('fr')}</td>
                  <td>{t.partner_name || <span style={{ color: '#94a3b8' }}>-</span>}</td>
                  <td style={{ color: '#64748b' }}>{t.client_name || '-'}</td>
                  <td style={{ color: '#94a3b8', fontSize: 12 }}>{t.client_email || '-'}</td>
                  <td>
                    <strong style={{ color: '#ef4444' }}>-{t.points_amount?.toLocaleString('fr')} pts</strong>
                  </td>
                  <td style={{ fontSize: 11, color: '#94a3b8' }}>{t.idempotency_key || '-'}</td>
                  <td><span className={statusClass[t.status] || 'badge-active'}>{statusLabel[t.status] || t.status || 'completed'}</span></td>
                </tr>
              ))}
              {consumeTxns.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Aucune consommation</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Partenaire</th>
                <th>Pack</th>
                <th>Points</th>
                <th>Montant payé</th>
                <th>Devise</th>
              </tr>
            </thead>
            <tbody>
              {b2bTxns.map(t => (
                <tr key={t.id}>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>#{t.id}</code></td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{new Date(t.created_at).toLocaleString('fr')}</td>
                  <td><strong>{t.partner_name}</strong></td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: 30, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#3b82f6' }}>
                      {t.pack_name || '-'}
                    </span>
                  </td>
                  <td>{t.points_amount?.toLocaleString('fr')} pts</td>
                  <td><strong style={{ color: '#10b981' }}>{t.amount ? `${parseFloat(t.amount).toFixed(2)} TND` : '-'}</strong></td>
                  <td>{t.currency || 'TND'}</td>
                </tr>
              ))}
              {b2bTxns.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Aucun achat B2B</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TransactionsAdmin;
